import { useEffect, useRef, useSyncExternalStore } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  WS_CLIENT_TYPING_NAME,
  WS_EVENT_NAME,
  type WsEvent,
  type WsEventOf,
  type WsEventType,
} from '@support/shared';

import { apiOrigin, getAccessToken, onAccessToken, refreshSession } from './api';
import { invalidateRealtimeCaches } from './query';

/**
 * Realtime панели.
 *
 * Три вещи, из-за которых этот файл существует именно в таком виде:
 *
 *   1. Токен уезжает в handshake.auth, а НЕ в query. Строка запроса оседает в
 *      логах прокси и в истории браузера, а это access-токен. Сервер query и
 *      не читает (см. extractToken в realtime.gateway).
 *   2. Подписка на диалоги — только после support:ready. Аутентификация на
 *      сервере асинхронна (подпись, потом пользователь из БД), и
 *      conversation.subscribe, отправленный раньше, получает unauthorized.
 *      Поэтому подписки копятся и разворачиваются по ready.
 *   3. При переподключении кэш сбрасывается. Пока сокет лежал, события
 *      терялись — список диалогов и счётчики остаются из прошлого. Без сброса
 *      оператор смотрит на правдоподобные устаревшие данные и не понимает,
 *      почему новое сообщение «не пришло».
 */

// ──────────────────────────── Имена событий ────────────────────────────

/** Сервер → клиент, помимо WS_EVENT_NAME. См. realtime.constants.ts. */
const READY_EVENT = 'support:ready';
const ERROR_EVENT = 'support:error';

/** Клиент → сервер. */
const CLIENT_EVENTS = {
  subscribe: 'conversation.subscribe',
  unsubscribe: 'conversation.unsubscribe',
  typing: 'conversation.typing',
} as const;

export interface ReadyPayload {
  userId: string;
  /** Проекты, комнаты которых занял сокет. Считаны сервером по membership. */
  projects: string[];
  at: string;
}

export type SocketStatus = 'idle' | 'connecting' | 'ready' | 'offline';

// ──────────────────────────── Состояние модуля ────────────────────────────

let socket: Socket | null = null;
let status: SocketStatus = 'idle';
let ready: ReadyPayload | null = null;
/** Было ли уже успешное рукопожатие: отличает первый вход от переподключения. */
let hadSession = false;

/**
 * Отложенная попытка после отказа сервера.
 *
 * Отдельная от встроенного reconnection socket.io: тот НЕ переподключается,
 * если соединение разорвал сам сервер (reason 'io server disconnect'), — а
 * гейтвей делает это штатно, на каждой ревалидации и на любом сбое
 * рукопожатия. Без своей попытки панель после такого разрыва замолкает
 * навсегда: событий нет, ошибки нет, данные тихо стареют.
 */
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let retryAttempt = 0;

/** Диалоги, на которые панель хочет быть подписанной. Источник истины — он. */
const wanted = new Set<string>();
/** Диалоги, подписка на которые сервером подтверждена. */
const subscribed = new Set<string>();

type EventHandler = (event: WsEvent) => void;
const handlers = new Map<WsEventType, Set<EventHandler>>();
const statusListeners = new Set<() => void>();

/** Кто уже печатал в диалоге на момент подписки — см. sendSubscribe. */
type TypingSnapshotHandler = (conversationId: string, userIds: string[]) => void;
const typingSnapshotHandlers = new Set<TypingSnapshotHandler>();

function setStatus(next: SocketStatus): void {
  if (status === next) return;
  status = next;
  for (const listener of statusListeners) listener();
}

// ──────────────────────────── Подключение ────────────────────────────

/**
 * Поднять сокет. Идемпотентно: повторный вызов при живом соединении ничего не
 * делает, а при мёртвом — переподключает с новым токеном.
 */
export function connectSocket(): void {
  const token = getAccessToken();
  // Без токена рукопожатие всё равно отклонят, а socket.io уйдёт в вечный
  // цикл переподключений и завалит логи сервера.
  if (!token) return;

  if (socket) {
    socket.auth = { token };
    if (!socket.connected) socket.connect();
    return;
  }

  socket = io(apiOrigin(), {
    // Токен в auth рукопожатия: query оседает в логах прокси.
    auth: { token },
    // Путь по умолчанию /socket.io — он же проксируется Vite в деве.
    transports: ['websocket', 'polling'],
    autoConnect: false,
    reconnection: true,
    // Полсекунды до первой попытки: короткий сетевой провал не должен быть
    // заметен оператору вовсе.
    reconnectionDelay: 500,
    // Потолок — 10 секунд. Дальше растить нечего: на том конце живой сервер,
    // а не перегруженный, и минутные паузы означали бы минуту без событий.
    reconnectionDelayMax: 10_000,
    // Разброс, чтобы полсотни вкладок после перезапуска API не пришли в
    // одну и ту же миллисекунду.
    randomizationFactor: 0.5,
    reconnectionAttempts: Infinity,
    timeout: 10_000,
  });

  bind(socket);
  setStatus('connecting');
  socket.connect();
}

/**
 * Своя попытка переподключения — с растущей паузой, потолок 30 секунд.
 *
 * Секунда на первую: сервер, который только что отключил нас сам, скорее всего
 * перезапускается, и штурмовать его сразу же незачем.
 */
function scheduleReconnect(): void {
  if (retryTimer !== undefined) return;
  const delay = Math.min(1000 * 2 ** retryAttempt, 30_000);
  retryAttempt += 1;
  retryTimer = setTimeout(() => {
    retryTimer = undefined;
    connectSocket();
  }, delay);
}

function cancelReconnect(): void {
  if (retryTimer !== undefined) clearTimeout(retryTimer);
  retryTimer = undefined;
  retryAttempt = 0;
}

export function disconnectSocket(): void {
  cancelReconnect();
  wanted.clear();
  subscribed.clear();
  ready = null;
  hadSession = false;
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  setStatus('idle');
}

function bind(instance: Socket): void {
  instance.on('connect', () => {
    // Ещё не ready: сервер только принял транспорт, пользователя он проверит
    // асинхронно. Подписываться на этом этапе нельзя.
    setStatus('connecting');
  });

  instance.on(READY_EVENT, (payload: ReadyPayload) => {
    ready = payload;
    setStatus('ready');
    cancelReconnect();

    // Переподключение, а не первый вход: события за время разрыва потеряны.
    if (hadSession) invalidateRealtimeCaches();
    hadSession = true;

    // Комнаты диалогов сервер при разрыве забыл — восстанавливаем их сами.
    subscribed.clear();
    for (const conversationId of wanted) sendSubscribe(conversationId);
  });

  instance.on(ERROR_EVENT, (payload: { code?: string; message?: string }) => {
    // Токен протух, пока вкладка спала: обновляемся и заходим заново. Сервер
    // уже разорвал соединение, поэтому reconnect тут не сработает сам.
    if (payload?.code === 'unauthorized') {
      void refreshSession().then((ok) => {
        if (ok) connectSocket();
      });
    }
  });

  instance.on('disconnect', (reason: string) => {
    ready = null;
    subscribed.clear();
    setStatus('offline');

    // Обрыв транспорта socket.io чинит сам. А вот 'io server disconnect' —
    // это разрыв по инициативе сервера, и встроенный reconnection на него
    // не срабатывает по замыслу библиотеки. Гейтвей рвёт соединение штатно
    // (ревалидация, сбой рукопожатия), поэтому возвращаться нужно самим.
    // Токена нет — возвращаться незачем: сессии больше не существует.
    if (reason === 'io server disconnect' && getAccessToken()) scheduleReconnect();
  });

  instance.on('connect_error', () => {
    setStatus('offline');
  });

  instance.on(WS_EVENT_NAME, (event: WsEvent) => {
    dispatch(event);
  });
}

/** Смена токена (обновление или выход) — сокет обязан её увидеть. */
onAccessToken((token) => {
  if (!socket) return;
  if (!token) {
    disconnectSocket();
    return;
  }
  socket.auth = { token };
});

// ──────────────────────────── Подписки на диалоги ────────────────────────────

/**
 * Подписаться на тред.
 *
 * Вызывать можно в любой момент, в том числе до подключения: желание
 * запоминается и разворачивается по support:ready. Иначе открытый по прямой
 * ссылке диалог остался бы без typing и без статусов доставки — а заметно это
 * стало бы только по «сообщение не отправилось».
 */
export function subscribeConversation(conversationId: string): void {
  wanted.add(conversationId);
  if (status === 'ready') sendSubscribe(conversationId);
}

export function unsubscribeConversation(conversationId: string): void {
  wanted.delete(conversationId);
  if (!subscribed.delete(conversationId)) return;
  socket?.emit(CLIENT_EVENTS.unsubscribe, { conversationId });
}

function sendSubscribe(conversationId: string): void {
  if (!socket || subscribed.has(conversationId)) return;
  socket.emit(
    CLIENT_EVENTS.subscribe,
    { conversationId },
    (ack: { ok: boolean; typing?: string[]; error?: string }) => {
      if (ack?.ok) {
        subscribed.add(conversationId);
        if (ack.typing?.length) {
          // Кто печатает прямо сейчас — сервер отдаёт вместе с подтверждением.
          // Без этого коллега «появляется» только на следующем нажатии клавиши,
          // а до тех пор оператор уверен, что тикет свободен.
          const userIds = ack.typing;
          for (const handler of [...typingSnapshotHandlers]) {
            handler(conversationId, userIds);
          }
        }
      } else {
        // Отказ — диалога нет или он в чужом проекте. Повторять бессмысленно.
        wanted.delete(conversationId);
      }
    },
  );
}

/** «Оператор печатает». Telegram о наборе текста клиентом не сообщает. */
export function sendTyping(conversationId: string, typing: boolean): void {
  if (status !== 'ready') return;
  socket?.emit(WS_CLIENT_TYPING_NAME, { conversationId, typing });
}

// ──────────────────────────── Раздача событий ────────────────────────────

function dispatch(event: WsEvent): void {
  const listeners = handlers.get(event.type);
  if (!listeners) return;
  for (const handler of [...listeners]) {
    try {
      handler(event);
    } catch (error) {
      // Один упавший обработчик не должен унести остальных: иначе ошибка в
      // счётчике непрочитанных гасит и дорисовку сообщения.
      console.error(`[socket] обработчик ${event.type} упал`, error);
    }
  }
}

function addHandler(type: WsEventType, handler: EventHandler): () => void {
  const existing = handlers.get(type);
  // const, а не let: замыкание отписки видит объявленный тип переменной, и на
  // `let ... | undefined` сужение до Set внутрь неё не попадает.
  const set = existing ?? new Set<EventHandler>();
  if (!existing) handlers.set(type, set);

  set.add(handler);
  return () => {
    set.delete(handler);
    if (set.size === 0) handlers.delete(type);
  };
}

/**
 * Подписка компонента на событие конкретного типа.
 *
 * Обработчик держится в ref, а эффект зависит только от типа: иначе каждая
 * перерисовка родителя пересоздавала бы подписку, а с ней и гонку «событие
 * пришло между отпиской и подпиской».
 */
export function useSocketEvent<T extends WsEventType>(
  type: T,
  handler: (event: WsEventOf<T>) => void,
): void {
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    return addHandler(type, (event) => {
      ref.current(event as WsEventOf<T>);
    });
  }, [type]);
}

/** Подписка на несколько типов сразу — типизация до объединения веток. */
export function useSocketEvents(types: readonly WsEventType[], handler: (event: WsEvent) => void): void {
  const ref = useRef(handler);
  ref.current = handler;
  // Ключ по содержимому: массив литералов пересоздаётся на каждый рендер, и
  // ссылочная зависимость перевешивала бы подписки без конца.
  const key = types.join('|');

  useEffect(() => {
    const offs = key.split('|').map((type) => addHandler(type as WsEventType, (e) => ref.current(e)));
    return () => {
      for (const off of offs) off();
    };
  }, [key]);
}

/**
 * Кто печатал в диалоге в момент подписки.
 *
 * Отдельный канал, а не синтетическое presence.typing: у события есть
 * projectId, а на момент ack мы его не знаем, и подставленная пустая строка
 * рано или поздно попала бы в фильтр по проекту.
 */
export function useTypingSnapshot(
  handler: (conversationId: string, userIds: string[]) => void,
): void {
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    const listener: TypingSnapshotHandler = (conversationId, userIds) =>
      ref.current(conversationId, userIds);
    typingSnapshotHandlers.add(listener);
    return () => {
      typingSnapshotHandlers.delete(listener);
    };
  }, []);
}

/**
 * Подписка на тред на время жизни компонента.
 *
 * null — ничего не подписываем: экран диалога открывается и без выбранного
 * тикета.
 */
export function useConversationSubscription(conversationId: string | null): void {
  useEffect(() => {
    if (!conversationId) return;
    subscribeConversation(conversationId);
    return () => unsubscribeConversation(conversationId);
  }, [conversationId]);
}

// ──────────────────────────── Состояние для UI ────────────────────────────

function subscribeStatus(listener: () => void): () => void {
  statusListeners.add(listener);
  return () => statusListeners.delete(listener);
}

/** Состояние соединения для индикатора «нет связи». */
export function useSocketStatus(): SocketStatus {
  return useSyncExternalStore(
    subscribeStatus,
    () => status,
    () => 'idle',
  );
}

export function socketStatus(): SocketStatus {
  return status;
}

/** Проекты, которые сервер подтвердил для этого сокета. */
export function socketProjects(): string[] {
  return ready?.projects ?? [];
}
